import type { ZodType } from "zod";

import { apiErrorSchema, type ApiError as ApiErrorBody } from "@ravnar/api-contract";

import { env } from "../env";

export class ApiError extends Error {
	readonly status: number;
	readonly issues: ApiErrorBody["issues"];

	constructor(status: number, message: string, issues?: ApiErrorBody["issues"]) {
		super(message);
		this.name = "ApiError";
		this.status = status;
		this.issues = issues;
	}
}

export interface RequestOptions {
	readonly method?: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
	readonly query?: Record<string, string>;
	readonly body?: unknown;
	readonly signal?: AbortSignal;
}

/** Typed fetch: every response is parsed with its contract schema, every failure becomes an ApiError. */
export async function apiRequest<T>(
	path: string,
	schema: ZodType<T>,
	{ method = "GET", query, body, signal }: RequestOptions = {},
): Promise<T> {
	const url = new URL(`${env.VITE_API_ORIGIN}${path}`, window.location.origin);
	if (query) {
		url.search = new URLSearchParams(query).toString();
	}
	const response = await fetch(url, {
		method,
		headers: body === undefined ? undefined : { "content-type": "application/json" },
		body: body === undefined ? undefined : JSON.stringify(body),
		signal,
	});
	let payload: unknown;
	try {
		payload = await response.json();
	} catch {
		payload = undefined;
	}
	if (!response.ok) {
		const parsed = apiErrorSchema.safeParse(payload);
		throw new ApiError(
			response.status,
			parsed.success ? parsed.data.error : `Request failed with status ${response.status}`,
			parsed.success ? parsed.data.issues : undefined,
		);
	}
	return schema.parse(payload);
}
